import { APITypesV1 } from "@cur8/api-client";
import {
  BottomSheet,
  Button,
  Icon16AlertTriangle,
  Icon16ArrowLeft,
  Icon16Checkmark,
  Icon16Close,
  Icon24Alert,
  IconButton,
  SegmentedControl,
  SegmentedControlItem,
  Typography,
} from "@cur8/maneki";
import { Measurement } from "@cur8/rich-entity";
import { UserURI } from "@cur8/uri";
import { useCallback, useMemo, useState } from "react";
import { useStoreMeasurementMutation } from "hooks/mutations/useStoreMeasurementMutation";
import { useRiskRangesQuery } from "hooks/queries/useRiskRangesQuery";
import { useVisitMeasurementsQuery } from "hooks/queries/useVisitMeasurementsQuery";
import { useMSAL } from "render/context/MSALContext";
import { useFeaturePreference } from "render/hooks/useFeaturePreference";
import { Link } from "render/ui/trigger/Link";
import { GroupTile, GroupTileStatus } from "./GroupTile";
import { getMeasurementCaution } from "./measurementCaution";
import {
  CONCIERGE_GENERATOR,
  eyePressureGroup,
  getMeasurementConfigs,
  getMeasurementGrid,
  getPreferImperial,
  getRequiredMeasurementConfigs,
  getWearsContactLenses,
  isGroup,
  MetricConfig,
  MetricGroup,
  WEARS_CONTACT_LENSES_LABEL,
  WEARS_CONTACT_LENSES_METRIC_NAME,
} from "./measurementConfig";
import { MeasurementEntry } from "./MeasurementEntry";
import { MeasurementTile } from "./MeasurementTile";
import styles from "./styles.module.sass";

// Bigger touch target for iPad operators: the shipped segment padding
// (--spacing-xs vertically) is too thin to tap comfortably.
const TALL_SEGMENT_STYLE = {
  padding: "var(--spacing-m) var(--spacing-l)",
};

interface MeasurementsBottomSheetProps {
  patientId: string;
  visitId: string;
  countryCode: string | undefined;
  generation: APITypesV1.RoomGeneration | undefined;
  isOpen: boolean;
  onClose: () => void;
}

export function MeasurementsBottomSheet({
  patientId,
  visitId,
  countryCode,
  generation,
  isOpen,
  onClose,
}: MeasurementsBottomSheetProps) {
  const [activeGroup, setActiveGroup] = useState<MetricGroup | null>(null);
  const [activeMetric, setActiveMetric] = useState<MetricConfig | null>(null);

  // Per-metric unit choice for this open sheet, so a tile reflects what was
  // actually toggled and saved rather than reverting to the country default.
  const [unitOverrides, setUnitOverrides] = useState<Map<string, boolean>>(
    () => new Map()
  );
  const setMetricShowImperial = useCallback(
    (metricName: string, showImperial: boolean) => {
      setUnitOverrides((prev) => new Map(prev).set(metricName, showImperial));
    },
    []
  );

  const configs = useMemo(
    () => getMeasurementConfigs(generation),
    [generation]
  );

  const grid = useMemo(() => getMeasurementGrid(generation), [generation]);

  const metricNames = useMemo(
    () => configs.map((c) => c.metricName),
    [configs]
  );

  const { data: measurementSummary } = useVisitMeasurementsQuery({
    patientId,
    visitId,
    options: { refetchInterval: 30_000 },
  });

  const { data: riskRanges } = useRiskRangesQuery({ metricNames });

  const storeMeasurement = useStoreMeasurementMutation({ patientId, visitId });
  // Dedicated instance: the contact-lens write must not share isPending/
  // isError/reset() with individual metric saves, otherwise backing out of
  // an unrelated entry can silently clear a real contact-lens save failure.
  const storeContactLensAnswer = useStoreMeasurementMutation({
    patientId,
    visitId,
  });
  const { auth } = useMSAL();
  const { data: featureFlags } = useFeaturePreference();
  const retestAlertsEnabled = featureFlags.pressureRetestAlertsEnabled;

  const existingByMetric = useMemo(() => {
    const map = new Map<string, Measurement>();
    for (const m of measurementSummary?.measurements ?? []) {
      if (m.metricName) {
        map.set(m.metricName, m);
      }
    }
    return map;
  }, [measurementSummary]);

  // Optimistic: while our own contact-lens write is in flight, trust its
  // variables over measurementSummary, which only reflects the mutation
  // once it resolves. Without this the control would visually revert (and
  // the tiles would stay enabled) for the length of the network round-trip.
  const wearsContactLenses = useMemo(() => {
    if (
      storeContactLensAnswer.isPending &&
      storeContactLensAnswer.variables?.metricName ===
        WEARS_CONTACT_LENSES_METRIC_NAME
    ) {
      return storeContactLensAnswer.variables.dimensions[0]?.value === 1;
    }
    return getWearsContactLenses(measurementSummary?.measurements);
  }, [
    storeContactLensAnswer.isPending,
    storeContactLensAnswer.variables,
    measurementSummary,
  ]);
  const hasContactLenses = wearsContactLenses === true;
  // Blocks eye pressure entry unless the operator has confirmed "no" —
  // an unanswered question must not silently let tonometry proceed.
  const blocksEyePressureEntry = wearsContactLenses !== false;

  const requiredConfigs = useMemo(
    () => getRequiredMeasurementConfigs(configs, hasContactLenses),
    [configs, hasContactLenses]
  );

  const handleWearsContactLensesChange = useCallback(
    (value: boolean) => {
      if (!auth) {
        return;
      }
      storeContactLensAnswer.mutate({
        sourceUri: new UserURI(auth.tenantId, auth.uniqueId).toString(),
        generator: CONCIERGE_GENERATOR,
        metricName: WEARS_CONTACT_LENSES_METRIC_NAME,
        timestampStart: new Date().toISOString(),
        type: null,
        dimensions: [{ name: "value", value: value ? 1 : 0, unit: "boolean" }],
        visitId,
      });
    },
    [auth, storeContactLensAnswer, visitId]
  );

  // One place, so precedence is explicit: a skipped group has no values to
  // question, so the skip wins.
  const getGroupStatus = useCallback(
    (group: MetricGroup): GroupTileStatus | undefined => {
      if (group === eyePressureGroup && hasContactLenses) {
        return { kind: "note", text: "Skipped due to hard contact lenses" };
      }
      if (!retestAlertsEnabled) {
        return undefined;
      }
      const caution = getMeasurementCaution(group, existingByMetric);
      if (caution) {
        return {
          kind: "caution",
          title: caution.title,
          difference: caution.difference,
          implausibleMetricNames: caution.implausibleMetricNames,
        };
      }
      return undefined;
    },
    [hasContactLenses, existingByMetric, retestAlertsEnabled]
  );

  // The hub tile catches the eye, but the prompt has to survive the tap through.
  const activeGroupCaution = useMemo(
    () =>
      activeGroup && retestAlertsEnabled
        ? getMeasurementCaution(activeGroup, existingByMetric)
        : null,
    [activeGroup, existingByMetric, retestAlertsEnabled]
  );

  const completedCount = useMemo(
    () =>
      requiredConfigs.filter((c) => existingByMetric.has(c.metricName)).length,
    [requiredConfigs, existingByMetric]
  );

  const hubTitle = `Measurements (${completedCount}/${requiredConfigs.length})`;

  const roomDescription = useMemo(() => {
    if (generation === APITypesV1.RoomGeneration.Gen1) {
      return "You are in a Gen1 room – please enter measurements manually.";
    }
    if (generation === APITypesV1.RoomGeneration.Gen2) {
      return "You are in a Gen2 room – once the devices send measurements you will see them appearing. You can still manually override measurements if required.";
    }
    return undefined;
  }, [generation]);

  const isAutoImportedMetric = (metric: MetricConfig) =>
    !!metric.autoImported && generation === APITypesV1.RoomGeneration.Gen2;

  // Back from entry form: return to group if one is active, otherwise hub
  const handleBackFromEntry = useCallback(() => {
    storeMeasurement.reset();
    setActiveMetric(null);
  }, [storeMeasurement]);

  // Back from group grid: return to hub
  const handleBackFromGroup = useCallback(() => {
    setActiveGroup(null);
  }, []);

  const handleClose = useCallback(() => {
    setActiveMetric(null);
    setActiveGroup(null);
    onClose();
  }, [onClose]);

  const tileGrid = activeGroup ? activeGroup.metrics : null;

  return (
    <BottomSheet isOpen={isOpen} onClose={handleClose} showCloseIcon={false}>
      <div className={styles.content}>
        {activeMetric ? (
          <>
            <div className={styles.breadcrumbRow}>
              <div className={styles.breadcrumb}>
                <Link onPress={handleBackFromEntry}>
                  <span className={styles.breadcrumbLink}>
                    <span className={styles.breadcrumbIcon}>
                      <Icon16ArrowLeft />
                    </span>
                    <Typography variant="label-s">
                      {activeGroup ? activeGroup.label : "Measurements"}
                    </Typography>
                  </span>
                </Link>
                <span className={styles.breadcrumbCurrent}>
                  <Typography variant="label-s">
                    / {activeMetric.label}
                  </Typography>
                </span>
              </div>
              <IconButton
                aria-label="Close"
                variant="tertiary"
                onPress={handleClose}
              >
                <Icon16Close />
              </IconButton>
            </div>
            <MeasurementEntry
              config={activeMetric}
              patientId={patientId}
              visitId={visitId}
              isAutoImported={isAutoImportedMetric(activeMetric)}
              existing={existingByMetric.get(activeMetric.metricName)}
              riskRanges={
                riskRanges?.find(
                  (r) => r.metricName === activeMetric.metricName
                )?.riskRanges
              }
              countryCode={countryCode}
              showImperial={
                unitOverrides.get(activeMetric.metricName) ??
                getPreferImperial(activeMetric, countryCode)
              }
              onToggleImperial={(value) =>
                setMetricShowImperial(activeMetric.metricName, value)
              }
              rejectImpossible={retestAlertsEnabled}
              storeMeasurement={storeMeasurement}
              onBack={handleBackFromEntry}
              onDone={handleBackFromEntry}
            />
          </>
        ) : tileGrid ? (
          <>
            <div className={styles.breadcrumbRow}>
              <div className={styles.breadcrumb}>
                <Link onPress={handleBackFromGroup}>
                  <span className={styles.breadcrumbLink}>
                    <span className={styles.breadcrumbIcon}>
                      <Icon16ArrowLeft />
                    </span>
                    <Typography variant="label-s">Measurements</Typography>
                  </span>
                </Link>
                <span className={styles.breadcrumbCurrent}>
                  <Typography variant="label-s">
                    / {activeGroup!.label}
                  </Typography>
                </span>
              </div>
              <IconButton
                aria-label="Close"
                variant="tertiary"
                onPress={handleClose}
              >
                <Icon16Close />
              </IconButton>
            </div>
            <div className={styles.entryTitle}>
              <Typography variant="display-s">{activeGroup!.label}</Typography>
            </div>
            {activeGroup === eyePressureGroup && (
              <div className={styles.groupQuestion}>
                <div
                  className={styles.cautionBox}
                  data-caution={blocksEyePressureEntry || undefined}
                >
                  <div className={styles.cautionBoxHeader}>
                    <Icon16AlertTriangle />
                    <Typography variant="label-s">
                      {WEARS_CONTACT_LENSES_LABEL}
                    </Typography>
                  </div>
                  <SegmentedControl
                    ariaLabel={WEARS_CONTACT_LENSES_LABEL}
                    UNSAFE_style={{
                      backgroundColor: "var(--color-bg-secondary)",
                      border:
                        wearsContactLenses === undefined
                          ? "2px solid var(--color-border-subtlest)"
                          : "2px solid var(--color-border-brand-strong)",
                    }}
                    selectedKeys={
                      wearsContactLenses === undefined
                        ? []
                        : [wearsContactLenses ? "yes" : "no"]
                    }
                    onSelectionChange={(keys) => {
                      const key = [...keys][0];
                      if (key === "yes" || key === "no") {
                        handleWearsContactLensesChange(key === "yes");
                      }
                    }}
                  >
                    <SegmentedControlItem
                      id="no"
                      UNSAFE_style={TALL_SEGMENT_STYLE}
                    >
                      No
                    </SegmentedControlItem>
                    <SegmentedControlItem
                      id="yes"
                      UNSAFE_style={TALL_SEGMENT_STYLE}
                    >
                      Yes
                    </SegmentedControlItem>
                  </SegmentedControl>
                  <Typography variant="body-s">
                    {wearsContactLenses === undefined
                      ? "Please submit an answer in order to proceed"
                      : wearsContactLenses
                        ? "Yes, the member is wearing hard contact lenses. Skip testing"
                        : "No, the member isn't wearing hard contact lenses. Proceed with testing"}
                  </Typography>
                </div>
                {storeContactLensAnswer.isError && (
                  <Typography
                    variant="body-s"
                    color="warning"
                    UNSAFE_style={{ paddingTop: "var(--spacing-s)" }}
                  >
                    Failed to save the contact lens answer. Please try again.
                  </Typography>
                )}
              </div>
            )}
            {activeGroupCaution && (
              <div className={styles.groupQuestion}>
                <div className={styles.cautionBox} data-caution>
                  <div className={styles.cautionBoxHeader}>
                    <Icon16AlertTriangle />
                    {/* color="default" is load-bearing: Typography renders
                        data-color={color} with no fallback, so omitting the
                        prop leaves the text inheriting .cautionBoxHeader's red
                        rather than falling back to primary. The icon keeps the
                        red on its own. */}
                    <Typography variant="label-s" color="default">
                      {activeGroupCaution.bannerTitle}
                    </Typography>
                  </div>
                  <Typography variant="body-s">
                    {activeGroupCaution.sentences.join(" ")}
                  </Typography>
                  {activeGroupCaution.difference && (
                    <>
                      <div className={styles.cautionBoxDivider} />
                      <div className={styles.cautionStats}>
                        <div className={styles.cautionStat}>
                          <Typography variant="eyebrow-s" color="subtle">
                            {activeGroupCaution.difference.label}
                          </Typography>
                          <span className={styles.statValueFlagged}>
                            {activeGroupCaution.difference.amount} mmHg
                          </span>
                        </div>
                        <div className={styles.cautionStat}>
                          <Typography variant="eyebrow-s" color="subtle">
                            Limit
                          </Typography>
                          <Typography variant="label-m">
                            {activeGroupCaution.difference.limitIsInclusive
                              ? "≥"
                              : ">"}
                            {activeGroupCaution.difference.limit} mmHg
                          </Typography>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
            <div className={styles.tileGrid}>
              {tileGrid.map((config) => (
                <MeasurementTile
                  key={config.metricName}
                  config={config}
                  measurement={existingByMetric.get(config.metricName)}
                  countryCode={countryCode}
                  unitOverrides={unitOverrides}
                  flagImplausible={retestAlertsEnabled}
                  disabled={
                    activeGroup === eyePressureGroup && blocksEyePressureEntry
                  }
                  onClick={() => setActiveMetric(config)}
                />
              ))}
            </div>
          </>
        ) : (
          <>
            <div className={styles.breadcrumbRow}>
              <div className={styles.entryTitle}>
                <Typography variant="display-s">{hubTitle}</Typography>
              </div>
              <IconButton
                aria-label="Close"
                variant="tertiary"
                onPress={handleClose}
              >
                <Icon16Close />
              </IconButton>
            </div>
            {roomDescription && (
              <Typography variant="body-m" color="subtle">
                {roomDescription}
              </Typography>
            )}
            <div className={styles.tileGrid}>
              {grid.map((item) =>
                isGroup(item) ? (
                  <GroupTile
                    key={item.label}
                    group={item}
                    existingByMetric={existingByMetric}
                    countryCode={countryCode}
                    unitOverrides={unitOverrides}
                    status={getGroupStatus(item)}
                    onClick={() => setActiveGroup(item)}
                  />
                ) : (
                  <MeasurementTile
                    key={item.metricName}
                    config={item}
                    measurement={existingByMetric.get(item.metricName)}
                    countryCode={countryCode}
                    unitOverrides={unitOverrides}
                    flagImplausible={retestAlertsEnabled}
                    onClick={() => setActiveMetric(item)}
                  />
                )
              )}
            </div>
          </>
        )}
      </div>

      {activeGroup != null && activeMetric == null ? (
        <Button variant="secondary" onPress={handleBackFromGroup} fillContainer>
          Back
        </Button>
      ) : undefined}

      {activeMetric == null &&
      activeGroup == null &&
      completedCount === requiredConfigs.length ? (
        <Button variant="primary" onPress={handleClose} fillContainer>
          Done
        </Button>
      ) : undefined}
    </BottomSheet>
  );
}

export function MeasurementsChip({
  patientId,
  visitId,
  generation,
  countryCode,
}: {
  patientId: string;
  visitId: string;
  generation: APITypesV1.RoomGeneration | undefined;
  countryCode: string | undefined;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const configs = useMemo(
    () => getMeasurementConfigs(generation),
    [generation]
  );

  const { data: measurementSummary } = useVisitMeasurementsQuery({
    patientId,
    visitId,
    options: { refetchInterval: 30_000 },
  });

  const hasContactLenses = useMemo(
    () => getWearsContactLenses(measurementSummary?.measurements) === true,
    [measurementSummary]
  );

  const requiredConfigs = useMemo(
    () => getRequiredMeasurementConfigs(configs, hasContactLenses),
    [configs, hasContactLenses]
  );

  const completedCount = useMemo(() => {
    const metricNames = new Set(requiredConfigs.map((c) => c.metricName));
    return (
      measurementSummary?.measurements.filter(
        (m) => m.metricName && metricNames.has(m.metricName)
      ).length ?? 0
    );
  }, [requiredConfigs, measurementSummary]);

  const chipState =
    completedCount === requiredConfigs.length
      ? "done"
      : completedCount > 0
        ? "partial"
        : "none";

  return (
    <>
      <button
        type="button"
        className={styles.chip}
        data-state={chipState}
        onClick={() => setIsOpen(true)}
      >
        <Typography variant="body-xs">
          {chipState === "done"
            ? `Measurements done`
            : `Measurements - ${requiredConfigs.length - completedCount} missing`}
        </Typography>
        {chipState === "done" ? <Icon16Checkmark /> : <Icon24Alert />}
      </button>

      <MeasurementsBottomSheet
        patientId={patientId}
        visitId={visitId}
        countryCode={countryCode}
        generation={generation}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
}
